import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle2, HelpCircle, BookOpen } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { Breadcrumbs, breadcrumbJsonLd } from "../components/site/Breadcrumbs";
import { usePageMeta, SITE_PUBLIC_URL } from "../components/site/usePageMeta";
import { pageBySlug, ALL_PAGES, LOWCOUNTRY_CITIES, CONNECTION_STEPS } from "../content/hubs";
import { IMAGES } from "../data";
import NotFound from "./NotFound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGES = {
  "demolition": { src: IMAGES.exterior, alt: "Selective demolition work inside a Lowcountry home" },
  "home-additions": { src: IMAGES.addition, alt: "Newly built home addition exterior in South Carolina" },
  "kitchen-and-bath-remodels": { src: IMAGES.kitchen, alt: "Remodeled kitchen with new cabinetry and finishes" },
  "complete-home-remodels": { src: IMAGES.remodel, alt: "Complete home remodel with open interior spaces" },
  "home-building": { src: IMAGES.blueprint, alt: "Blueprints for a custom home build in the Lowcountry" },
};

const CTAButton = ({ testid, children, dark }) => (
  <a
    href="/#request"
    data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2 ${
      dark ? "bg-[#54BCE6] text-[#133955] hover:bg-white" : "bg-[#133955] text-white hover:bg-[#54BCE6] hover:text-[#133955]"
    }`}
  >
    {children}
    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

export default function ServicePage() {
  const { group, slug } = useParams();
  const page = pageBySlug(group, slug);
  const [guides, setGuides] = React.useState([]);

  React.useEffect(() => {
    if (!page) return;
    fetch(`${API}/blog/posts`)
      .then((r) => r.json())
      .then((d) => {
        const rel = (d.posts || []).filter((p) => (p.tags || []).some((t) => page.guideTags.includes(t)));
        setGuides(rel);
      })
      .catch(() => setGuides([]));
  }, [page]);

  const path = page ? `/sc/${group}/${slug}/` : null;
  const hubLabel = group === "major-projects" ? "Major Projects" : "Home Services";
  const crumbs = [
    { label: "Home", href: "/" },
    { label: hubLabel, href: `/sc/${group}/` },
    { label: page?.name || "" },
  ];
  usePageMeta({
    title: page?.metaTitle || "Home Pro Connect",
    description: page?.metaDescription || "",
    path,
    jsonLd: page ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: `${page.name} contractor connections`,
          serviceType: page.name,
          description: page.metaDescription,
          url: `${SITE_PUBLIC_URL}${path}`,
          provider: { "@type": "Organization", name: "Home Pro Connect", url: SITE_PUBLIC_URL },
          areaServed: LOWCOUNTRY_CITIES.map((c) => ({ "@type": "City", name: `${c}, SC` })),
        },
        {
          "@type": "FAQPage",
          mainEntity: page.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        breadcrumbJsonLd(crumbs),
      ],
    } : null,
  });

  if (!page) return <NotFound />;

  const heroImg = HERO_IMAGES[slug];
  const relatedProjects = page.relatedProjects.map((s) => ALL_PAGES.find((p) => p.group === "major-projects" && p.slug === s)).filter(Boolean);
  const relatedServices = page.relatedServices.map((s) => ALL_PAGES.find((p) => p.group === "home-services" && p.slug === s)).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main data-testid="service-page" data-slug={slug}>
        {/* Hero */}
        <section className="bg-[#133955] text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-14 md:pt-32 md:pb-16">
            <Breadcrumbs items={crumbs.map((c) => ({ ...c, }))} />
            <div className="mt-6 grid lg:grid-cols-12 gap-10 items-center">
              <div className={heroImg ? "lg:col-span-7" : "lg:col-span-12"}>
                <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">{page.h1}</h1>
                {page.intro.map((p, i) => (
                  <p key={i} className="mt-4 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">{p}</p>
                ))}
                <div className="mt-8"><CTAButton testid="service-cta-top" dark>Request a Connection</CTAButton></div>
              </div>
              {heroImg && (
                <div className="lg:col-span-5">
                  <img src={heroImg.src} alt={heroImg.alt} width="800" height="600" loading="eager" className="rounded-[1.5rem] shadow-[0_24px_60px_rgba(0,0,0,0.35)] aspect-[4/3] object-cover w-full" />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Includes / common jobs */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight">{page.includesTitle}</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-5xl">
              {page.includes.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-xl bg-[#EAF7FC] px-5 py-4">
                  <CheckCircle2 size={18} className="text-[#54BCE6] shrink-0 mt-0.5" />
                  <p className="text-[#173042]/85 text-[15px]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-14 md:py-20 bg-[#EAF7FC]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight">{page.stepsTitle}</h2>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {CONNECTION_STEPS.map((s) => (
                <div key={s.num} className="rounded-2xl bg-white p-7 border border-[rgba(19,57,85,0.08)]">
                  <span className="font-display text-4xl font-extrabold text-[#54BCE6]">{s.num}</span>
                  <h3 className="mt-3 font-display text-xl font-bold text-[#133955]">{s.title}</h3>
                  <p className="mt-2 text-[#173042]/75 text-[15px] leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-10"><CTAButton testid="service-cta-mid">Tell Us About Your Project</CTAButton></div>
          </div>
        </section>

        {/* Considerations */}
        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight">Things to consider before you start</h2>
            <div className="mt-8 space-y-5">
              {page.considerations.map((c) => {
                const [head, rest] = c.split(/:(.+)/);
                return (
                  <p key={head} className="text-[#173042]/85 text-[15px] md:text-base leading-relaxed">
                    <strong className="text-[#133955]">{head}:</strong> {rest}
                  </p>
                );
              })}
            </div>
          </div>
        </section>

        {/* Service area */}
        <section className="py-14 md:py-20 bg-[#133955] text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-2 text-[#54BCE6] font-bold text-xs uppercase tracking-[0.2em] mb-4"><MapPin size={15} /> Service Area</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Serving the Lowcountry & Grand Strand</h2>
            <p className="mt-4 text-white/75 max-w-2xl">We connect homeowners with pros across the South Carolina Lowcountry, including:</p>
            <ul className="mt-6 flex flex-wrap gap-2.5">
              {LOWCOUNTRY_CITIES.map((c) => (
                <li key={c} className="rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-sm font-semibold">{c}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ — answers rendered as real text in the HTML */}
        <section className="py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-2 text-[#54BCE6] font-bold text-xs uppercase tracking-[0.2em] mb-4"><HelpCircle size={15} /> Common Questions</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight">{page.name} FAQs</h2>
            <div className="mt-8 divide-y divide-[rgba(19,57,85,0.08)]">
              {page.faqs.map((f) => (
                <div key={f.q} className="py-5">
                  <h3 className="font-bold text-[#133955] text-lg">{f.q}</h3>
                  <p className="mt-2 text-[#173042]/80 text-[15px] leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Helpful guides (blog) — hidden when nothing relates yet */}
        {guides.length > 0 && (
          <section className="pb-14 md:pb-20">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-2 text-[#54BCE6] font-bold text-xs uppercase tracking-[0.2em] mb-4"><BookOpen size={15} /> Helpful Guides</div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#133955] tracking-tight">From our blog</h2>
              <ul className="mt-6 space-y-3">
                {guides.map((g) => (
                  <li key={g.id}>
                    <Link to={`/blog/${g.slug}`} data-testid={`guide-link-${g.slug}`} className="group flex items-center justify-between gap-4 rounded-xl border border-[rgba(19,57,85,0.1)] px-5 py-4 hover:border-[#54BCE6] transition-colors">
                      <span className="font-semibold text-[#133955] group-hover:text-[#54BCE6] transition-colors">{g.title}</span>
                      <ArrowRight size={16} className="text-[#54BCE6] shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Related links */}
        <section className="pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="grid sm:grid-cols-2 gap-8">
              {relatedProjects.length > 0 && (
                <div>
                  <h2 className="font-display text-xl font-bold text-[#133955]">Related major projects</h2>
                  <ul className="mt-4 space-y-2.5">
                    {relatedProjects.map((r) => (
                      <li key={r.slug}>
                        <a href={`/sc/major-projects/${r.slug}/`} data-testid={`related-project-${r.slug}`} className="text-[#54BCE6] font-semibold hover:text-[#133955] transition-colors">
                          {r.name} contractor connections in the Lowcountry
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {relatedServices.length > 0 && (
                <div>
                  <h2 className="font-display text-xl font-bold text-[#133955]">Related home services</h2>
                  <ul className="mt-4 space-y-2.5">
                    {relatedServices.map((r) => (
                      <li key={r.slug}>
                        <a href={`/sc/home-services/${r.slug}/`} data-testid={`related-service-${r.slug}`} className="text-[#54BCE6] font-semibold hover:text-[#133955] transition-colors">
                          {r.name} specialist connections in the Lowcountry
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-14 md:py-20 bg-[#EAF7FC]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#133955] tracking-tight">Ready to talk about your {page.name.toLowerCase().replace(/s$/, "")} project?</h2>
            <p className="mt-4 text-[#173042]/75 max-w-xl mx-auto">One request is all it takes. Tell us what you're planning and we'll work to connect you with the right pro for the job.</p>
            <div className="mt-8"><CTAButton testid="service-cta-bottom">Request a Connection</CTAButton></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
